/**
 * The doctor's secretary.
 *
 * Until this upgrade the secretary was a *mode* on the doctor's own login —
 * `LiveView` has a toggle, and both people shared one account and one Gmail.
 * The requirement makes them a separate person with their own account, which
 * changes two things that are not obvious from the screen:
 *
 * The secretary never says which doctor they serve. `servesDoctorId` lives on
 * their membership and the server resolves it on every request; the browser
 * does not send it and would be refused if it did. That is what stops a
 * secretary at one hospital reading a queue at another.
 *
 * And the doctor's "finished" signal arrives by poll, because there is no
 * websocket layer in this product. Five seconds, paused while the tab is
 * hidden — which is `usePoll`'s existing behaviour and worth knowing rather
 * than working around: a minimised reception screen catches up on the next
 * look, it does not stream. The screen therefore shows when it last synced, so
 * "the doctor has not finished anybody" is distinguishable from "this screen
 * stopped talking to the server".
 *
 * Dashboard 6 of the upgrade adds the secretary's other work as tabs: run the
 * AI over the day's files in one go (the summaries land on the doctor's
 * screen), shift the doctor's schedule with the same preview-then-apply the
 * clinics manager uses and follow up every moved patient, and keep the
 * patients' files current and hand them their portal link. Every one of those
 * is scoped server-side to the one doctor this account serves.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { STATUS_LABELS } from '../../domain/bookings';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  Panel,
  Spinner,
  Tabs,
} from '../../components/common';
import { useAsync } from '../../hooks/useAsync';
import { usePoll } from '../../hooks/usePolling';
import { useNotifications } from '../../context/NotificationContext';
import { usePermissions } from '../../context/PermissionContext';
import { bookingApi, operationsApi, syncApi } from '../../api/endpoints';
import { AiAnalyzeButton } from '../ai/AiAnalyzeButton';
import { HeroBand, StatTile3D, Tile3D } from '../../components/common/Tile3D';
import { PERMISSIONS } from '../../domain/rbac';
import { todayKey } from '../../domain/dates';
import { AiBatchPanel } from '../hospitalShared/AiBatchPanel';
import { PrintDayFilesButton } from '../hospitalShared/DayFilesPrint';
import { NoticeTracker } from '../hospitalShared/NoticeTracker';
import { PatientFinder } from '../hospitalShared/PatientFinder';
import { PatientWorkspace } from '../hospitalShared/PatientWorkspace';
import { DelayModal } from '../operations/OperationsBoard';
import { LiveQueueBoard } from '../queue/LiveQueueBoard';

/**
 * "معك اليوم: د. فلان" — the doctor the clinics desk handed this secretary
 * today (the day sheet), or their standing doctor. The server resolves it on
 * every request; this only says it out loud, so a secretary moved to another
 * doctor this morning sees it before they call anyone in.
 */
function TodayDoctorChip({ date }) {
  const board = useAsync(() => operationsApi.board(date), [date]);
  const doctor = (board.data?.doctors || [])[0];
  if (!doctor) return null;
  return (
    <Badge className="today-doctor-chip" icon="stethoscope">
      معك اليوم: د. {String(doctor.name || '').replace(/^د\.\s*/, '')}
    </Badge>
  );
}

/** The doctor's clinics today, each with the delay tile, and the follow-up list. */
function SecretaryDelay({ date }) {
  const { success, error: toastError } = useNotifications();
  const board = useAsync(() => operationsApi.board(date), [date]);
  const [delayFor, setDelayFor] = useState(null);
  const [noticesKey, setNoticesKey] = useState(0);

  const clinics = board.data?.clinics || [];
  const countFor = (clinicId) =>
    (board.data?.bookings || []).filter((b) => String(b.clinicId) === String(clinicId) && b.status !== 'cancelled').length;

  return (
    <div className="space-y-6">
      {board.error ? (
        <ErrorState error={board.error} onRetry={board.reload} />
      ) : board.loading && !board.data ? (
        <Spinner label="جارٍ تحميل العيادات" variant="inline" />
      ) : clinics.length === 0 ? (
        <EmptyState icon="building-2" title="لا توجد عيادات للطبيب اليوم" />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {clinics.map((clinic, i) => (
            <Tile3D
              key={clinic.id}
              icon="timer"
              label={`تأجيل ${clinic.name}`}
              hint={`${countFor(clinic.id)} حجز اليوم`}
              tone="amber"
              pulse
              enter
              delay={i}
              onClick={() => setDelayFor({ clinic })}
            />
          ))}
        </div>
      )}

      <div>
        <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
          <Icon name="bell-ring" size={16} className="text-amber-300" /> متابعة المرضى بعد التأجيل
        </h3>
        <NoticeTracker refreshKey={noticesKey} />
      </div>

      <DelayModal
        column={delayFor}
        date={date}
        onClose={() => setDelayFor(null)}
        onShifted={() => {
          board.reload();
          setNoticesKey((k) => k + 1);
        }}
        notify={{ success, toastError }}
      />
    </div>
  );
}

/** The doctor's patients' files: edit, append, print, hand over the portal. */
function SecretaryFiles() {
  const [selected, setSelected] = useState(null);
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[22rem_1fr]">
      <Panel className="h-fit">
        {/*
          One file at a time on the right; the whole day as a printed set from
          here. The requirement asks for both, and the set is the one a doctor
          actually carries in. No doctorId is passed: the server scopes a
          secretary to the doctor she serves.
        */}
        <div className="mb-3">
          <PrintDayFilesButton date={todayKey()} label="طباعة ملفات اليوم كلها" />
        </div>
        <PatientFinder selectedId={selected?.id} onSelect={setSelected} canCreate={false} />
      </Panel>
      <Panel className="min-w-0">
        {selected ? (
          <PatientWorkspace
            key={selected.id}
            patient={selected}
            sections={['medical', 'file', 'portal']}
            defaultSection="medical"
            onPatientChange={setSelected}
          />
        ) : (
          <EmptyState icon="folder-open" title="اختر ملف مريض" message="تظهر هنا ملفات مرضى الطبيب الذي تعمل معه." />
        )}
      </Panel>
    </div>
  );
}

/** The legacy value was 2s; 5s is gentler and still faster than a queue moves. */
const POLL_MS = 5000;

export function SecretaryQueue() {
  const { success, error: toastError } = useNotifications();
  const { can, servesDoctorId, loading: permsLoading, serviceShown } = usePermissions();

  const [session, setSession] = useState(null);
  const [syncedAt, setSyncedAt] = useState(null);
  const [stale, setStale] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('queue');

  const date = todayKey();
  const doctorId = servesDoctorId;

  const bookings = useAsync(
    () => (doctorId ? bookingApi.listFresh({ doctorId, date }) : Promise.resolve([])),
    [doctorId, date],
  );

  /** Cancelled visits leave the queue entirely, as they do on the doctor's screen. */
  const queue = useMemo(
    () =>
      (bookings.data || [])
        .filter((b) => b.status !== 'cancelled')
        .sort((a, b) => String(a.time).localeCompare(String(b.time))),
    [bookings.data],
  );

  const applySession = useCallback((payload) => {
    if (!payload) return;
    const state = Object.values(payload)[0];
    if (!state) return;
    setSession(state);
    setSyncedAt(new Date());
    setStale(false);
  }, []);

  usePoll(
    () => syncApi.getLiveSession(),
    POLL_MS,
    {
      enabled: Boolean(doctorId),
      onData: applySession,
      // A poll failure is otherwise swallowed into console.warn, and a
      // secretary whose screen has quietly stopped syncing looks identical to
      // one whose doctor has not finished anybody yet.
      onError: () => setStale(true),
    },
  );

  useEffect(() => {
    if (!doctorId) return;
    syncApi.getLiveSession().then(applySession).catch(() => setStale(true));
  }, [doctorId, applySession]);

  const liveIdx = session?.liveIdx ?? -1;
  const phase = session?.phase;

  const current = liveIdx >= 0 ? queue[liveIdx] : null;
  const next = queue[liveIdx + 1] || null;
  const finished = phase === 'finished';

  /** Send the next patient in, and clear the finished flag as we go. */
  const callNext = useCallback(async () => {
    if (!next) return;
    setBusy(true);
    try {
      await syncApi.setLiveSession({
        liveDate: date,
        liveIdx: liveIdx + 1,
        liveBookingId: next.id,
        phase: 'in_room',
      });
      await bookingApi.updateStatus(next.id, 'in_progress');
      success(`تم استدعاء ${next.patientName}`);
      bookings.reload();
      const fresh = await syncApi.getLiveSession();
      applySession(fresh);
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  }, [next, liveIdx, date, bookings, applySession, success, toastError]);

  if (permsLoading) return <Spinner label="جارٍ التحقق من الصلاحيات" />;

  if (!doctorId) {
    return (
      <EmptyState
        icon="user-x"
        title="لم يتم ربط حسابك بطبيب"
        message="تواصل مع إدارة المنشأة لربط حسابك بالطبيب الذي تعمل معه."
      />
    );
  }

  if (bookings.error) return <ErrorState error={bookings.error} onRetry={bookings.reload} />;

  const waiting = queue.length - Math.max(0, liveIdx + 1);

  return (
    <div>
      <HeroBand
        icon="monitor-check"
        tone="teal"
        title="طابور الكشف"
        subtitle="تابع الطبيب، وحضّر المريض التالي قبل أن يدخل الغرفة."
      >
        <TodayDoctorChip date={date} />
        <Badge
          className={
            stale
              ? 'border-red-500/30 bg-red-500/10 text-red-300'
              : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
          }
        >
          {stale
            ? 'انقطع الاتصال'
            : `آخر تحديث ${syncedAt ? syncedAt.toLocaleTimeString('ar-EG') : '—'}`}
        </Badge>
      </HeroBand>

      <div className="mb-5 grid grid-cols-3 gap-3 sm:gap-4">
        <StatTile3D label="في الانتظار" value={waiting} icon="timer" tone="amber" delay={0} />
        <StatTile3D
          label="تم الكشف"
          value={Math.max(0, liveIdx)}
          icon="check-circle-2"
          tone="emerald"
          delay={1}
        />
        <StatTile3D label="إجمالي اليوم" value={queue.length} icon="users" tone="cyan" delay={2} />
      </div>

      {/*
        The signal the whole screen exists for. Deliberately the loudest thing
        on the page when it appears: a secretary is not staring at this screen,
        they are talking to somebody at a desk, and they need to notice from
        across the room that the doctor has finished. Above the tabs, so it is
        seen whichever one is open.
      */}
      {finished && (
        <Panel className="mb-5 border-2 border-emerald-500/50 bg-emerald-500/10">
          <div className="flex flex-wrap items-center gap-4">
            <span className="tile3d__face tone-emerald tile3d--pulse shrink-0">
              <Icon name="check-circle-2" size={26} className="tile3d__icon" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-lg font-bold text-white">أنهى الطبيب الكشف</p>
              <p className="text-sm text-slate-300">
                {next ? `التالي: ${next.patientName} — ${next.time}` : 'لا يوجد مريض تالٍ'}
              </p>
            </div>
            {next && can(PERMISSIONS.QUEUE_ADVANCE) && (
              <Button onClick={callNext} loading={busy} icon="arrow-left">
                أدخل المريض التالي
              </Button>
            )}
          </div>
        </Panel>
      )}

      <Tabs
        tabs={[
          { id: 'queue', label: 'الطابور الآن', icon: 'monitor-check' },
          // The clinic's numbered turns — the ones on the hall screen (QA report §5.3).
          ...(can(PERMISSIONS.QUEUE_ADVANCE) ? [{ id: 'turns', label: 'أدوار العيادة والشاشة', icon: 'list-ordered' }] : []),
          ...(can(PERMISSIONS.AI_ANALYST) && serviceShown('aiBatch') ? [{ id: 'ai', label: 'التحليل الذكي للملفات', icon: 'sparkles' }] : []),
          ...(can(PERMISSIONS.SCHEDULE_SHIFT) ? [{ id: 'delay', label: 'تأجيل ومتابعة المرضى', icon: 'timer' }] : []),
          ...(can(PERMISSIONS.PATIENT_RECORD_VIEW) ? [{ id: 'files', label: 'ملفات المرضى', icon: 'folder-open' }] : []),
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'turns' && <LiveQueueBoard compact />}
      {tab === 'ai' && <AiBatchPanel bookings={queue} date={date} />}
      {tab === 'delay' && <SecretaryDelay date={date} />}
      {tab === 'files' && <SecretaryFiles />}

      {tab === 'queue' && (
      <>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Who is in the room */}
        <Panel>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
            <Icon name="stethoscope" size={16} className="text-emerald-400" />
            داخل الكشف
          </h2>
          {current ? (
            <div>
              <p className="text-lg font-bold text-white">{current.patientName}</p>
              <p className="text-sm text-slate-400">
                {current.time} · {STATUS_LABELS[current.status] || current.status}
              </p>
            </div>
          ) : (
            <p className="text-sm text-slate-500">لم يبدأ الكشف بعد.</p>
          )}
        </Panel>

        {/* Prepare the next one — the part the requirement is specific about */}
        <Panel>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
            <Icon name="user-plus" size={16} className="text-cyan-400" />
            تحضير المريض التالي
          </h2>

          {next ? (
            <div className="space-y-3">
              <div>
                <p className="text-lg font-bold text-white">{next.patientName}</p>
                <p className="text-sm text-slate-400">{next.time}</p>
              </div>

              {/*
                The AI runs BEFORE the patient enters the room, which is what
                the requirement asks for. The secretary holds AI_ANALYST for
                exactly this; the report lands on the doctor's screen because
                both read the same analysis, not because it is forwarded.
              */}
              {can(PERMISSIONS.AI_ANALYST) && (
                <AiAnalyzeButton
                  patientId={next.patientId}
                  manualPatientCode={next.manualPatientCode}
                  patientName={next.patientName}
                />
              )}

              {can(PERMISSIONS.QUEUE_ADVANCE) && (
                <Tile3D
                  icon="arrow-left"
                  label="أدخله الآن"
                  hint="ينبّه شاشة الطبيب"
                  tone="teal"
                  enter
                  disabled={busy}
                  onClick={callNext}
                />
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-500">لا يوجد مريض تالٍ في طابور اليوم.</p>
          )}
        </Panel>
      </div>

      <Panel padded={false} className="mt-5">
        <div className="flex items-center justify-between border-b border-white/5 p-4">
          <h2 className="text-sm font-bold text-white">طابور اليوم</h2>
          <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300">
            {waiting} في الانتظار
          </Badge>
        </div>

        {queue.length === 0 ? (
          <div className="p-6">
            <EmptyState icon="calendar-x" title="لا حجوزات اليوم" message="" />
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {queue.map((b, i) => (
              <li
                key={b.id}
                className={`flex items-center gap-3 p-3 ${
                  i === liveIdx ? 'bg-emerald-500/10' : i < liveIdx ? 'opacity-45' : ''
                }`}
              >
                <span className="w-6 shrink-0 text-center font-mono text-xs text-slate-500">
                  {i + 1}
                </span>
                <span className="shrink-0 rounded-lg bg-slate-800 px-2 py-1 font-mono text-xs text-cyan-300">
                  {b.time}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-white">{b.patientName}</span>
                {i === liveIdx && (
                  <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                    في الغرفة
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>
      </>
      )}
    </div>
  );
}

export default SecretaryQueue;
